import React, { useContext, useState } from "react";
import { AuthContext } from "../../../Context/AuthContextProvider";
import toast from "react-hot-toast";
import {
  User,
  Mail,
  Phone,
  Calendar,
  Save,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  Edit3,
} from "lucide-react";

function EditAccountDetails() {
  const { loginUser, formatDate, updateUserData } = useContext(AuthContext);
  const [firstName, setFirstName] = useState(loginUser.firstName || "");
  const [lastName, setLastName] = useState(loginUser.lastName || "");
  const [mobileNumber, setMobileNumber] = useState(
    loginUser.mobile_number || ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    mobileNumber: "",
  });

  const handleMobileNumberChange = (e) => {
    const number = e.target.value;
    setMobileNumber(number);
    validateMobileNumber(number);
  };

  const validateMobileNumber = (number) => {
    const mobileRegex = /^[6789]\d{9}$/;
    if (!mobileRegex.test(number)) {
      setErrors((prev) => ({
        ...prev,
        mobileNumber: "Must be 10 digits starting with 6, 7, 8, or 9",
      }));
    } else {
      setErrors((prev) => ({ ...prev, mobileNumber: "" }));
    }
  };

  const handleSaveChanges = async () => {
    if (
      firstName === loginUser.firstName &&
      lastName === loginUser.lastName &&
      mobileNumber === loginUser.mobile_number
    ) {
      toast.error("Please modify at least one field to update!");
      return;
    }

    if (errors.mobileNumber) {
      toast.error("Mobile number format is invalid");
      return;
    }

    try {
      setIsLoading(true);
      await updateUserData(loginUser._id, firstName, lastName, mobileNumber);
    } catch (error) {
      console.error("Error updating user data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDiscardChanges = () => {
    setFirstName(loginUser.firstName || "");
    setLastName(loginUser.lastName || "");
    setMobileNumber(loginUser.mobile_number || "");
    setErrors({ mobileNumber: "" });
    toast.success("Changes discarded");
  };

  const handleKeyPress = (e) => {
    const charCode = e.charCode;
    if (charCode < 48 || charCode > 57) {
      e.preventDefault();
    }
  };

  const hasChanges =
    firstName !== loginUser.firstName ||
    lastName !== loginUser.lastName ||
    mobileNumber !== loginUser.mobile_number;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 p-4 md:p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-xl shadow-lg">
            <Edit3 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Edit Profile Details
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Update your personal information
            </p>
          </div>
        </div>

        {/* Last Update Info */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Calendar className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-sm font-medium text-blue-900">Last Updated</p>
            <p className="text-xs text-blue-700">
              {formatDate(loginUser.LatestUpdate)}
            </p>
          </div>
        </div>

        {/* Edit Form Card */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-[#fcdb6a]/20 to-[#b99653]/10 px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-[#b99653] to-[#d4af37] rounded-lg">
                <User className="w-5 h-5 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">
                Personal Information
              </h2>
            </div>
          </div>

          {/* Form Content */}
          <div className="p-6 space-y-6">
            {/* First Name */}
            <div className="space-y-2">
              <label
                htmlFor="firstName"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <User className="w-4 h-4 text-[#b99653]" />
                First Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Enter your first name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
                {firstName !== loginUser.firstName && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Last Name */}
            <div className="space-y-2">
              <label
                htmlFor="lastName"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <User className="w-4 h-4 text-[#b99653]" />
                Last Name
              </label>
              <div className="relative">
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Enter your last name"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20 transition-all outline-none text-gray-900 font-medium"
                />
                {lastName !== loginUser.lastName && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                )}
              </div>
            </div>

            {/* Mobile Number */}
            <div className="space-y-2">
              <label
                htmlFor="mobileNumber"
                className="flex items-center gap-2 text-sm font-semibold text-gray-700"
              >
                <Phone className="w-4 h-4 text-[#b99653]" />
                Mobile Number
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 text-gray-500 font-medium">
                  <span>+91</span>
                  <span className="text-gray-300">|</span>
                </div>
                <input
                  type="tel"
                  id="mobileNumber"
                  value={mobileNumber}
                  onChange={handleMobileNumberChange}
                  onKeyPress={handleKeyPress}
                  maxLength="10"
                  placeholder="Enter 10-digit mobile number"
                  className={`w-full pl-20 pr-4 py-3 border-2 rounded-xl transition-all outline-none text-gray-900 font-medium ${
                    errors.mobileNumber
                      ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-500/20"
                      : "border-gray-200 focus:border-[#b99653] focus:ring-2 focus:ring-[#b99653]/20"
                  }`}
                />
                {mobileNumber !== loginUser.mobile_number &&
                  !errors.mobileNumber && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                {errors.mobileNumber && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  </div>
                )}
              </div>
              {errors.mobileNumber && (
                <div className="flex items-center gap-2 text-red-600 text-sm mt-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.mobileNumber}</span>
                </div>
              )}
            </div>

            {/* Email (Read-only) */}
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                <Mail className="w-4 h-4 text-[#b99653]" />
                Email Address
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={loginUser.email_id || ""}
                  disabled
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-500 font-medium cursor-not-allowed"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                  Read-only
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDiscardChanges}
                disabled={isLoading || !hasChanges}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  isLoading || !hasChanges
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-white border-2 border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
                }`}
              >
                <RotateCcw className="w-5 h-5" />
                <span>Discard Changes</span>
              </button>

              <button
                onClick={handleSaveChanges}
                disabled={isLoading || !hasChanges || errors.mobileNumber}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                  isLoading || !hasChanges || errors.mobileNumber
                    ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-[#b99653] to-[#d4af37] text-white hover:shadow-lg hover:scale-[1.02]"
                }`}
              >
                <Save className="w-5 h-5" />
                <span>{isLoading ? "Saving..." : "Save Changes"}</span>
              </button>
            </div>

            {/* Help Text */}
            {hasChanges && (
              <div className="mt-4 flex items-start gap-2 text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
                <AlertCircle className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
                <p>
                  You have unsaved changes. Click <strong>Save Changes</strong>{" "}
                  to update your profile or <strong>Discard Changes</strong> to
                  reset.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Security Notice */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-blue-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-blue-700" />
              </div>
              <div>
                <h3 className="font-bold text-blue-900 text-sm">
                  Security Notice
                </h3>
                <p className="text-xs text-blue-700 mt-1">
                  Email address cannot be changed for security reasons. Contact
                  support if you need assistance.
                </p>
              </div>
            </div>
          </div>

          {/* Verification Status */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-green-200 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-700" />
              </div>
              <div>
                <h3 className="font-bold text-green-900 text-sm">
                  Account Status
                </h3>
                <p className="text-xs text-green-700 mt-1">
                  {loginUser.isEmailVerified && loginUser.isMobileVerified
                    ? "Your account is fully verified"
                    : "Complete verification to unlock all features"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditAccountDetails;
